import classNames from "classnames";
import { useFormikContext } from "formik";
import React, { useState } from "react";
import { Accordion, Alert, Card, Form } from "react-bootstrap";

import { ICDMedical80D } from "../../../../config/interfaces";
import PreventEditComponent from "../../../PreventEditComponent";
import { SwitchBtnContextAwareToggle } from "../../../accordion";
import { FormField, NumericInputField } from "../../../fields";

const CD80DForm: React.FC = () => {
	const { values, errors, touched, setFieldValue, handleBlur } = useFormikContext<ICDMedical80D>();

	const [healthCheckupErrorMsg, setHealthCheckupErrorMsg] = useState<string>("");

	const getTotalMedicalInsurancePremium = (value1: string, value2: string) => {
		const total = Number(value1 || 0) + Number(value2 || 0);
		setFieldValue("TotalSection80D", total);
	};

	const getTotalMedicalExpenditure = (value1: string, value2: string) => {
		const total = Number(value1 || 0) + Number(value2 || 0);
		setFieldValue("TotalExpenditure", total);
	};

	const getTotalHealthCheckup = (value1: string, value2: string) => {
		const total = Number(value1 || 0) + Number(value2 || 0);
		setFieldValue("TotalHealthCheckup", total);
		if (total > 5000) {
			setHealthCheckupErrorMsg(
				"Total preventive health checkup for Parents and Self/Family cannot exceed ₹ 5,000"
			);
		} else {
			setHealthCheckupErrorMsg("");
		}
	};

	return (
		<section className="mb-5">
			{healthCheckupErrorMsg && (
				<Alert variant="danger" className="mb-4 size-sm">
					<h5 className="alert-title">{healthCheckupErrorMsg} </h5>
				</Alert>
			)}
			<Accordion flush activeKey={values.ForSelfAndFamily ? "0" : "1"}>
				<Card className="more-info-card shadow-none mb-5 overflow-hidden">
					<Card.Header className={classNames(!values?.ForSelfAndFamily && "border-0")}>
						<Card.Title>
							For Self & Family (spouse & children)?
							<SwitchBtnContextAwareToggle
								eventKey="0"
								name="ForSelfAndFamily"
								onChange={(checked: boolean) => {
									setFieldValue("ForSelfAndFamily", checked);
									setFieldValue("SelfAndFamilyMedicalExpenditure", "");
									setFieldValue("SelfAndFamilyHealthCheckup", "");
									setFieldValue("SelfAndFamilyMedicalInsurance", "");
									setFieldValue("SelfAndFamilySeniorCitizen", false);
									getTotalHealthCheckup("", "");
								}}
							/>
						</Card.Title>
					</Card.Header>
					<Accordion.Collapse eventKey="0">
						<>
							<Card.Body>
								<PreventEditComponent>
									<Form.Group className="form-group">
										<Form.Check
											role="button"
											type="checkbox"
											id="SelfAndFamilySeniorCitizen"
											className="align-items-center"
										>
											<Form.Check.Input
												type="checkbox"
												name="SelfAndFamilySeniorCitizen"
												checked={values.SelfAndFamilySeniorCitizen}
												onChange={(e) => {
													setFieldValue("SelfAndFamilySeniorCitizen", e.target.checked);
													setFieldValue("SelfAndFamilyMedicalExpenditure", "");
												}}
											/>
											<Form.Check.Label>
												You or Any of your family members (spouse / children) is a senior citizen
											</Form.Check.Label>
										</Form.Check>
									</Form.Group>
								</PreventEditComponent>

								<FormField label="Medical Insurance Premium" sm={4} isRequired={true}>
									<NumericInputField
										showLeftIcon={true}
										placeholder=""
										name="SelfAndFamilyMedicalInsurance"
										value={values.SelfAndFamilyMedicalInsurance || ""}
										onChange={(value) => {
											setFieldValue("SelfAndFamilyMedicalInsurance", value);
											getTotalMedicalInsurancePremium(value, values.ParentsMedicalInsurance);
										}}
										onBlur={handleBlur("SelfAndFamilyMedicalInsurance")}
										isInvalid={
											Boolean(errors.SelfAndFamilyMedicalInsurance) &&
											touched.SelfAndFamilyMedicalInsurance
										}
										errorMsg={errors.SelfAndFamilyMedicalInsurance}
									/>
								</FormField>

								<FormField label="Preventive Health Check-up" sm={4}>
									<NumericInputField
										showLeftIcon={true}
										placeholder=""
										name="SelfAndFamilyHealthCheckup"
										value={values.SelfAndFamilyHealthCheckup || ""}
										onChange={(value) => {
											setFieldValue("SelfAndFamilyHealthCheckup", value);
											getTotalHealthCheckup(value, values.ParentsHealthCheckup);
										}}
										onBlur={handleBlur("SelfAndFamilyHealthCheckup")}
										isInvalid={
											Boolean(errors.SelfAndFamilyHealthCheckup) &&
											touched.SelfAndFamilyHealthCheckup
										}
										errorMsg={errors.SelfAndFamilyHealthCheckup}
									/>
								</FormField>

								{values?.SelfAndFamilySeniorCitizen ? (
									<FormField label="Medical Expenditure" sm={4}>
										<NumericInputField
											showLeftIcon={true}
											placeholder=""
											name="SelfAndFamilyMedicalExpenditure"
											value={values.SelfAndFamilyMedicalExpenditure || ""}
											onChange={(value) => {
												setFieldValue("SelfAndFamilyMedicalExpenditure", value);
												getTotalMedicalExpenditure(value, values.ParentsMedicalExpenditure);
											}}
											onBlur={handleBlur("SelfAndFamilyMedicalExpenditure")}
											isInvalid={
												Boolean(errors.SelfAndFamilyMedicalExpenditure) &&
												touched.SelfAndFamilyMedicalExpenditure
											}
											errorMsg={errors.SelfAndFamilyMedicalExpenditure}
										/>
									</FormField>
								) : null}
							</Card.Body>
						</>
					</Accordion.Collapse>
				</Card>
			</Accordion>

			<Accordion flush activeKey={values.ForParents ? "0" : "1"}>
				<Card className="more-info-card shadow-none mb-5 overflow-hidden">
					<Card.Header className={classNames(!values?.ForParents && "border-0")}>
						<Card.Title>
							For Parents?
							<SwitchBtnContextAwareToggle
								eventKey="0"
								name="ForParents"
								onChange={(checked: boolean) => {
									setFieldValue("ForParents", checked);
									setFieldValue("ParentsMedicalExpenditure", "");
									setFieldValue("ParentsHealthCheckup", "");
									setFieldValue("ParentsMedicalInsurance", "");
									setFieldValue("ParentSeniorCitizen", false);
									getTotalHealthCheckup("", "");
								}}
							/>
						</Card.Title>
					</Card.Header>
					<Accordion.Collapse eventKey="0">
						<>
							<Card.Body>
								<PreventEditComponent>
									<Form.Group className="form-group">
										<Form.Check
											role="button"
											type="checkbox"
											id="ParentSeniorCitizen"
											className="align-items-center"
										>
											<Form.Check.Input
												type="checkbox"
												name="ParentSeniorCitizen"
												checked={values.ParentSeniorCitizen}
												onChange={(e) => {
													setFieldValue("ParentSeniorCitizen", e.target.checked);
													setFieldValue("ParentsMedicalExpenditure", "");
												}}
											/>
											<Form.Check.Label>Any of your Parents is a senior citizen</Form.Check.Label>
										</Form.Check>
									</Form.Group>
								</PreventEditComponent>
								<FormField label="Medical Insurance Premium" sm={4} isRequired={true}>
									<NumericInputField
										showLeftIcon={true}
										placeholder=""
										name="ParentsMedicalInsurance"
										value={values.ParentsMedicalInsurance || ""}
										onChange={(value) => {
											setFieldValue("ParentsMedicalInsurance", value);
											getTotalMedicalInsurancePremium(values.SelfAndFamilyMedicalInsurance, value);
										}}
										onBlur={handleBlur("ParentsMedicalInsurance")}
										isInvalid={
											Boolean(errors.ParentsMedicalInsurance) && touched.ParentsMedicalInsurance
										}
										errorMsg={errors.ParentsMedicalInsurance}
									/>
								</FormField>

								<FormField label="Preventive Health Check-up" sm={4}>
									<NumericInputField
										showLeftIcon={true}
										placeholder=""
										name="ParentsHealthCheckup"
										value={values.ParentsHealthCheckup || ""}
										onChange={(value) => {
											setFieldValue("ParentsHealthCheckup", value);
											getTotalHealthCheckup(values.SelfAndFamilyHealthCheckup, value);
										}}
										onBlur={handleBlur("ParentsHealthCheckup")}
										isInvalid={Boolean(errors.ParentsHealthCheckup) && touched.ParentsHealthCheckup}
										errorMsg={errors.ParentsHealthCheckup}
									/>
								</FormField>

								{values?.ParentSeniorCitizen ? (
									<FormField label="Medical Expenditure" sm={4}>
										<NumericInputField
											showLeftIcon={true}
											placeholder=""
											name="ParentsMedicalExpenditure"
											value={values.ParentsMedicalExpenditure || ""}
											onChange={(value) => {
												setFieldValue("ParentsMedicalExpenditure", value);
												getTotalMedicalExpenditure(values.SelfAndFamilyMedicalExpenditure, value);
											}}
											onBlur={handleBlur("ParentsMedicalExpenditure")}
											isInvalid={
												Boolean(errors.ParentsMedicalExpenditure) &&
												touched.ParentsMedicalExpenditure
											}
											errorMsg={errors.ParentsMedicalExpenditure}
										/>
									</FormField>
								) : null}
							</Card.Body>
						</>
					</Accordion.Collapse>
				</Card>
			</Accordion>
		</section>
	);
};

export default CD80DForm;
